import * as THREE from 'three';

const smooth = THREE.MathUtils.smoothstep;

/**
 * How much grass and moss grows on a patch of an island's ground, 0–1
 * (greener hills, Pieter 28 Sep 2026): on the gentle lower slopes, not on the
 * wet shore, not on the tops or steep faces (ash and rock stay there), and in
 * patches from `noise` (0–1) so no slope is one even sheet. Every edge is
 * smooth, so ash and grass never meet at a seam.
 */
export function grassCover(p: {
  /** Metres above the sea. */
  readonly height: number;
  /** The ground's normal's y: 1 is level, 0.7 is steep. */
  readonly up: number;
  readonly noise: number;
  /** Metres above the sea where grass starts, and where it is full. */
  readonly low: readonly [number, number];
  /** Where it starts to thin, and where it is gone. */
  readonly high: readonly [number, number];
}): number {
  const band = smooth(p.height, p.low[0], p.low[1]) * (1 - smooth(p.height, p.high[0], p.high[1]));
  const gentle = smooth(p.up, 0.8, 0.94);
  const patches = 0.35 + 0.65 * smooth(p.noise, 0.3, 0.7);
  return band * gentle * patches;
}

/** A `cover` attribute on a ground mesh: one weight per vertex, from its place and normal. */
export function withCover(
  geometry: THREE.BufferGeometry,
  weight: (x: number, z: number, y: number, up: number) => number,
): THREE.BufferGeometry {
  const position = geometry.getAttribute('position');
  if (!geometry.getAttribute('normal')) geometry.computeVertexNormals();
  const normal = geometry.getAttribute('normal');
  const cover = new Float32Array(position.count);
  for (let i = 0; i < position.count; i++) {
    cover[i] = weight(position.getX(i), position.getZ(i), position.getY(i), normal.getY(i));
  }
  geometry.setAttribute('cover', new THREE.BufferAttribute(cover, 1));
  return geometry;
}
